import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { Receipt, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { RoleTag } from "@/components/admin/RoleTag";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminFeeAudit() {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  const load = () => {
    setLoadErr(null);
    setData(null);
    api.get("/admin/fee-audit")
      .then(({ data }) => setData(data))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load the fee audit."));
  };
  useEffect(() => { load(); }, []);

  const rows = data?.orders || [];
  const clean = data && rows.length === 0;

  return (
    <div className="text-foreground" data-testid="admin-fee-audit">
      <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2">
        <Receipt className="w-7 h-7 text-olive-bright" /> Fee Audit
      </h2>
      <p className="text-sm font-body text-secondary-foreground mt-2 max-w-[720px]">
        Realized staff sales that were charged the <span className="text-foreground font-mono uppercase text-[11px]">Standard</span> rate
        ({data ? `${data.standard_rate_pct}%` : "6.5%"}) instead of the Pro/staff rate
        ({data ? `${data.elite_rate_pct}%` : "3.5%"}) staff accounts are entitled to. Read-only — nothing is refunded here.
      </p>
      <Crosshair className="my-5" />

      {loadErr && <LoadFailed reason={loadErr} onRetry={load} testid="fee-audit-load-failed" />}

      {!loadErr && !data && (
        <div className="border border-sniper bg-surface p-10 text-center font-mono uppercase text-sm text-muted-foreground">Loading…</div>
      )}

      {!loadErr && data && (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <div className="border border-sniper bg-surface p-4" data-testid="fee-audit-stat-staff">
              <div className="font-mono text-[10px] uppercase tracking-[2px] text-muted-foreground">Staff accounts</div>
              <div className="mt-1 font-heading text-2xl text-foreground">{data.staff_count}</div>
            </div>
            <div className="border border-sniper bg-surface p-4" data-testid="fee-audit-stat-orders">
              <div className="font-mono text-[10px] uppercase tracking-[2px] text-muted-foreground">Flagged sales</div>
              <div className="mt-1 font-heading text-2xl text-foreground">{data.total_orders}</div>
            </div>
            <div className={`border p-4 ${data.total_overcharge_cents > 0 ? "border-[#d3a24e]/60 bg-[#d3a24e]/5" : "border-sniper bg-surface"}`} data-testid="fee-audit-stat-total">
              <div className="font-mono text-[10px] uppercase tracking-[2px] text-muted-foreground">Total over-charged</div>
              <div className={`mt-1 font-heading text-2xl ${data.total_overcharge_cents > 0 ? "text-[#d3a24e]" : "text-foreground"}`}>{money(data.total_overcharge_cents)}</div>
            </div>
          </div>

          <div className="flex items-center mb-4">
            <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-sniper text-muted-foreground hover:text-foreground transition-colors font-mono uppercase text-xs" data-testid="fee-audit-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>

          {clean ? (
            <div className="border border-olive/40 bg-olive/5 p-10 text-center" data-testid="fee-audit-clean">
              <CheckCircle2 className="w-8 h-8 text-olive-bright mx-auto" />
              <p className="mt-3 font-heading text-lg text-foreground uppercase tracking-tight">All clear</p>
              <p className="mt-1 text-sm font-body text-secondary-foreground">No realized staff sales were charged the Standard rate.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="fee-audit-list">
              {rows.map((o) => (
                <div key={o.order_id} className="border border-sniper bg-surface p-4" data-testid={`fee-audit-row-${o.order_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <Link to={`/admin/orders`} className="font-heading text-base text-foreground hover:text-olive-bright">{o.listing_title || "Listing"}</Link>
                      <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                        <ShieldCheck className="w-3.5 h-3.5 text-olive-bright" />
                        <Link to={`/u/${o.seller_username}`} className="text-secondary-foreground hover:text-foreground">@{o.seller_username}</Link>
                        <RoleTag kind={o.seller_role} />
                        <span>· {o.order_id}</span>
                        {o.created_at && <span>· {String(o.created_at).slice(0, 10)}</span>}
                        <span className="text-muted-foreground">· {String(o.status || "").replace("_", " ")}</span>
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-[11px] text-muted-foreground">
                        charged <span className="text-foreground">{money(o.fee_charged_cents)}</span> ({o.rate_charged_pct}%)
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground">
                        Pro would be <span className="text-foreground">{money(o.would_be_elite_cents)}</span> ({data.elite_rate_pct}%)
                      </div>
                      <div className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase px-2 py-0.5 border border-[#d3a24e]/60 text-[#d3a24e]" data-testid={`fee-audit-overcharge-${o.order_id}`}>
                        <AlertTriangle className="w-3.5 h-3.5" /> over {money(o.overcharge_cents)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
